import { Classifier, ClassifierVersion } from '../../types'

/**
 * Going live always updates every profile at once, so the job of this card is to
 * make the impact obvious before the user commits.
 */
export default function LiveStatusCard({
    classifier,
    selectedVersion,
    draftVersionNumber,
    onGoLive,
    onRollback
}: {
    classifier: Classifier
    selectedVersion: ClassifierVersion
    draftVersionNumber: number
    onGoLive: () => void
    onRollback: () => void
}) {
    return (
        <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm text-gray-700">
                Live status
            </div>
            <div className="p-4 text-sm">
                <div className="flex justify-between py-1.5 border-b border-dashed border-gray-100">
                    <span className="text-gray-500">Currently live</span>
                    <span className="font-bold text-base text-gray-900">
                        v{classifier.liveVersion}
                    </span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-dashed border-gray-100">
                    <span className="text-gray-500">Profiles on this classifier</span>
                    <span className="font-bold">{classifier.profilesUsing}</span>
                </div>
                <div className="flex justify-between py-1.5">
                    <span className="text-gray-500">Across teams</span>
                    <span className="font-bold">{classifier.teamsUsing}</span>
                </div>

                {/* Impact warning — shown when there is a draft ready to go live */}
                <div className="mt-3 bg-suspect/10 border border-suspect/30 text-suspect rounded-lg p-2.5 text-xs">
                    Going live with <strong>v{draftVersionNumber}</strong> updates all{' '}
                    <strong>{classifier.profilesUsing} profiles</strong> currently using this
                    classifier.
                </div>

                <button
                    type="button"
                    onClick={onGoLive}
                    className="mt-3 w-full rounded-lg bg-arwen-600 px-3 py-2 text-sm font-semibold text-white hover:bg-arwen-700"
                >
                    Go Live with v{draftVersionNumber} <span aria-hidden="true">→</span>
                </button>
                {/* Roll back — only when an already-superseded (archived) version is
                    selected. Reuses the go-live blast-radius flow, reframed as reverting. */}
                {selectedVersion.status === 'archived' && (
                    <button
                        type="button"
                        onClick={onRollback}
                        className="mt-2 w-full rounded-lg border border-suspect/40 bg-white px-3 py-2 text-sm font-semibold text-suspect hover:bg-suspect/5"
                    >
                        <span aria-hidden="true">↺</span> Roll back to v
                        {selectedVersion.version} — {classifier.profilesUsing} profiles
                    </button>
                )}
            </div>
        </div>
    )
}
